// pages/components/billing/CurrentPlan.jsx
import React, { useState } from 'react';
import { Crown, Calendar, RefreshCw, XCircle, CheckCircle, AlertCircle } from 'lucide-react';
import apiRequest from '@/services/requests';

const CurrentPlan = ({ billingInfo, onUpdate }) => {
const [updatingRenew, setUpdatingRenew] = useState(false);
const [canceling, setCanceling] = useState(false);

if (!billingInfo) return null;

const { 
package: currentPackage,
package_expiry,
package_status,
package_auto_renew,
daysUntilExpiry,
currentPackageFeatures
} = billingInfo;

const packageColors = {
freemium: 'from-slate-400 to-slate-600',
standard: 'from-blue-400 to-blue-600',
premium: 'from-purple-400 to-purple-600',
elite: 'from-amber-400 to-amber-600'
};

const packageEmoji = {
freemium: '🆓',
standard: '⭐',
premium: '💎',
elite: '👑'
};

const handleToggleAutoRenew = async () => {
setUpdatingRenew(true);
try {
const res = await apiRequest.updateAutoRenew(!package_auto_renew);
if (res.success) {
onUpdate();
}
} catch (error) {
console.error('Error updating auto-renew:', error);
alert('Failed to update auto-renew setting');
}
setUpdatingRenew(false);
};

const handleCancelSubscription = async () => {
if (!confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.')) {
return;
}

setCanceling(true);
try {
const res = await apiRequest.cancelSubscription();
if (res.success) {
alert(res.message);
onUpdate();
}
} catch (error) {
console.error('Error canceling subscription:', error);
alert('Failed to cancel subscription');
}
setCanceling(false);
};

const formatDate = (date) => {
return new Date(date).toLocaleDateString('en-US', {
year: 'numeric',
month: 'long',
day: 'numeric'
});
};

const getStatusBadge = () => {
switch (package_status) {
case 'active':
return (
<div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
<CheckCircle size={16} />
Active
</div>
);
case 'cancelled':
return (
<div className="flex items-center gap-2 px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">
<AlertCircle size={16} />
Cancelled
</div>
);
case 'expired':
return (
<div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
<XCircle size={16} />
Expired
</div>
);
default:
return null;
}
};

return (
<div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
{/* Header with gradient */}
<div className={`bg-gradient-to-r ${packageColors[currentPackage]} p-8 text-white`}>
<div className="flex items-start justify-between">
<div>
<div className="flex items-center gap-3 mb-2">
<span className="text-xl lg:text-2xl">{packageEmoji[currentPackage]}</span>
<h2 className="text-xl lg:text-3xl font-bold">
{currentPackageFeatures?.name || currentPackage.charAt(0).toUpperCase() + currentPackage.slice(1)}
</h2>
</div>
<p className="text-white/90 text-sm lg:text-md">Your current subscription plan</p>
</div>
{getStatusBadge()}
</div>

{currentPackage !== 'freemium' && (
<div className="mt-6 flex items-center gap-8">
<div className="flex items-center gap-2">
<Crown size={20} />
<span className="text-xl lg:text-2xl font-bold">
${currentPackageFeatures?.monthly_price || 0}
<span className="text-sm font-normal opacity-90">/month</span>
</span>
</div>

{package_expiry && (
<div className="flex items-center gap-2">
<Calendar size={20} />
<div>
<p className="text-xs opacity-90">Renews on</p>
<p className="font-semibold text-sm">{formatDate(package_expiry)}</p>
</div>
</div>
)}
</div>
)}
</div>

{/* Content */}
<div className="p-8">
{/* Expiry Warning */}
{daysUntilExpiry !== null && daysUntilExpiry <= 7 && daysUntilExpiry > 0 && (
<div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
<div className="flex items-start gap-3">
<AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
<div>
<p className="font-semibold text-amber-900">
Your subscription expires in {daysUntilExpiry} {daysUntilExpiry === 1 ? 'day' : 'days'}
</p>
<p className="text-sm text-amber-700 mt-1">
{package_auto_renew 
? 'Your subscription will automatically renew.' 
: 'Enable auto-renew to continue enjoying premium features.'
}
</p>
</div>
</div>
</div>
)}

{/* Auto-Renew Toggle */}
{currentPackage !== 'freemium' && package_status === 'active' && (
<div className="mb-6 p-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
<div className="flex items-center justify-between">
<div className="flex items-start gap-3">
<div className="p-2 bg-indigo-100 rounded-lg">
<RefreshCw className="text-indigo-600" size={16} />
</div>
<div>
<h3 className="font-bold text-slate-900 mb-1">Automatic Renewal</h3>
<p className="text-sm text-slate-600">
{package_auto_renew 
? 'Your subscription will automatically renew on the expiry date'
: 'Enable to automatically renew your subscription each month'
}
</p>
</div>
</div>
<button
onClick={handleToggleAutoRenew}
disabled={updatingRenew}
className={`relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 disabled:opacity-50 ${
package_auto_renew ? 'bg-indigo-600' : 'bg-slate-300'
}`}
>
<span
className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
package_auto_renew ? 'translate-x-6' : 'translate-x-0'
}`}
/>
</button>
</div>
</div>
)}

{/* Features List */}
<div>
<h3 className="font-bold text-slate-900 mb-4 text-lg">Plan Features</h3>
<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
{currentPackageFeatures?.features.map((feature, idx) => (
<div
key={idx}
className={`flex items-center gap-3 p-3 rounded-lg ${
feature.included ? 'bg-green-50' : 'bg-slate-50'
}`}
>
<div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
feature.included ? 'bg-green-500' : 'bg-slate-300'
}`}>
{feature.included && (
<CheckCircle size={14} className="text-white" />
)}
</div>
<span className={`text-sm ${
feature.included ? 'text-slate-700 font-medium' : 'text-slate-400'
}`}>
{feature.name}
</span>
</div>
))}
</div>
</div>

{/* Cancel Subscription */}
{currentPackage !== 'freemium' && package_status === 'active' && (
<div className="mt-8 pt-6 border-t border-slate-200">
<button
onClick={handleCancelSubscription}
disabled={canceling}
className="text-red-600 hover:text-red-700 font-semibold text-sm disabled:opacity-50"
>
{canceling ? 'Canceling...' : 'Cancel Subscription'}
</button>
<p className="text-xs text-slate-500 mt-2">
You'll retain access until {package_expiry && formatDate(package_expiry)}
</p>
</div>
)}
</div>
</div>
);
};

export default CurrentPlan;